import { FC, useContext, useEffect, useRef, useState } from "react";
import orderBy from "lodash/orderBy";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { ClientListItem } from "@app/client/client";
import { ClientService } from "@app/client/clientService";
import { MinimalUser } from "@app/admin/user-administration/minimalUser";
import { TemplateCollectionMetadataModelWithActiveProjects } from "@app/admin/manage-templates/template";
import TemplateService from "@app/admin/manage-templates/templateService";

interface CreatePanelProps {
  currentUserid: number;
  defaultClient: ClientListItem;
  handleSave: (onClose: () => void, templateCollectionName: string, templateCount: number, isActive: boolean, selectedClientId: number, editUserIds: number[], templateId?: number) => void;
  allowClientEdit: boolean;
  loading: boolean;
  template?: TemplateCollectionMetadataModelWithActiveProjects;
}

const CreateEditPanelContent: FC<CreatePanelProps> = (props: CreatePanelProps) => {
  const flyoutContext = useContext(FlyoutContext);
  const templateService = useRef(new TemplateService()).current;
  const clientService = useRef(new ClientService()).current;
  const nameValidationIntervalRef = useRef<number>(null);

  const [clientOptions, setClientOptions] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [nameIsValid, setNameIsValid] = useState(true);
  const [nameIsValidText, setNameIsValidText] = useState("");
  const [selectedClientId, setSelectedClientId] = useState(props.defaultClient.clientId);
  const [selectedUsers, setSelectedUsers] = useState<MinimalUser[]>([]);
  const [templateCollectionName, setTemplateCollectionName] = useState("<Collection Name Goes Here>");
  const [templateCount] = useState(0);

  useEffect(() => {
    setLoading(true);
    const onMount = async () => {
      let clients: ClientListItem[] = [props.defaultClient];

      if (props.allowClientEdit) {
        clients = await clientService.getActiveClientsList();
      }

      handleClientChange(selectedClientId);

      setClientOptions(clients.map(x => <option key={x.clientId} value={x.clientId}>{x.name}</option>));
      setLoading(false);
    }

    onMount();
  }, []);

  const handleClientChange = async (clientId: number) => {
    let newAuthorizedUsers: MinimalUser[] = await templateService.getUsersAuthorizedForEdit(clientId);
    newAuthorizedUsers = orderBy(newAuthorizedUsers, (x) => x.userName, "asc");

    const newSelectedUsers = selectedUsers;
    if (!props.template?.templateCollectionId && !newSelectedUsers.some(x => x.userId === props.currentUserid)) {
      const currentUser = newAuthorizedUsers.find(x => x.userId === props.currentUserid);
      if (!!currentUser) {
        newSelectedUsers.push(currentUser);
      }
    }

    setSelectedUsers(newSelectedUsers.filter(su => newAuthorizedUsers.some(u => u.userId === su.userId)));
  }

  useEffect(() => {
    return () => {
      window.clearInterval(nameValidationIntervalRef.current);
    }
  }, []);

  const validateName = async (templateCollectionName: string, clientId: number) => {
    window.clearInterval(nameValidationIntervalRef.current);
    nameValidationIntervalRef.current = window.setTimeout(async () => {
      if (!templateCollectionName) {
        setNameIsValid(false);
        setNameIsValidText("");
        return;
      } else if (await templateService.templateNameExists(templateCollectionName.trim(), clientId, props.template?.templateCollectionId)) {
        setNameIsValid(false);
        setNameIsValidText("This templateCollectionName has already been used.");
      } else {
        setNameIsValid(true);
        setNameIsValidText("");
      }
    }, 500);
  }


  return (
    <>
      {props.loading || loading ? <LoadingSpinner /> :
        <>
          <QESTextInput
            testId={"create-manage-template-panel-content__name"}
            className="col-4"
            containerClassName="mb-2"
            label="Template Collection Name"
            value={templateCollectionName}
            onChange={(e) => {
              setTemplateCollectionName(e.target.value);
              validateName(e.target.value, selectedClientId);
            }}
            isValid={nameIsValid}
            errorText={nameIsValidText}
            required={true}
            wasFocusedExternally={!!props.template}
          />
          <QESSelect
            testId="create-manage-template-panel-content__client"
            containerClassName="col-4 mb-2"
            label="Client"
            value={selectedClientId}
            required={true}
            isValid={!!selectedClientId.toString()}
            disabled={!props.allowClientEdit}
            onChange={(e) => {
              setSelectedClientId(parseInt(e.target.value));
              handleClientChange(parseInt(e.target.value));
              validateName(templateCollectionName, parseInt(e.target.value));
            }}
          >
            {clientOptions}
          </QESSelect>

          <QESCheckbox
            containerClassName="mb-2"
            testId={"create-manage-template-panel-content__active"}
            label="Is Active"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />

          <FlyoutFooter
            submitButton={<QESButton
              qesTestId="create-manage-template-panel-content__confirm"
              className="mt10"
              //disabled={!nameIsValid || selectedUsers.length < 2}
              onClick={() => {
                props.handleSave(flyoutContext.handleCloseFlyout, templateCollectionName.trim(), templateCount, isActive, selectedClientId, selectedUsers.map(x => x.userId), props.template?.templateCollectionId);
              }}
            >Save</QESButton>
            }
          />
        </>
      }
    </>
  );
}

export default CreateEditPanelContent;
